import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { generateTagColor } from '@shared/components/Tag';
import { useUpdateWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { getLatestWorkflowTemplate, isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { uuid } from '@shared/util/uuid';

interface AddTag {
  loading: boolean;
  error?: string;
  addTag: (tagLabel: string) => Promise<API.TagComponentOption | undefined>;
}

export function useAddTag(workflowTemplateId: string, componentId: string): AddTag {
  const [error, setError] = useState<string>();
  const [updateWorkflowTemplate, { loading: updateLoading, error: updateError }] =
    useUpdateWorkflowTemplate();

  const addTag = useCallback(
    async (tagLabel: string) => {
      const { workflowTemplate } = await getLatestWorkflowTemplate(workflowTemplateId);
      const tagComponent = workflowTemplate?.components.find(
        component => component.id === componentId,
      );
      if (isNil(tagComponent) || !isTagComponent(tagComponent)) {
        setError('No component found.');
        return;
      }
      const newTag: NonNullable<API.TagComponentFragment['options']>[number] = {
        label: tagLabel,
        value: uuid(),
        backgroundColor: generateTagColor(tagComponent.options?.length ?? 0),
        archived: null,
      };
      const newTagComponent: API.TagComponentFragment = {
        ...tagComponent,
        options: [...(tagComponent.options ?? []), newTag],
      };
      const newWorkflowComponents =
        workflowTemplate?.components?.map(component => {
          return component.id !== componentId ? component : newTagComponent;
        }) ?? [];
      if (!isNil(workflowTemplate)) {
        const updateResult = await updateWorkflowTemplate({
          ...workflowTemplate,
          components: newWorkflowComponents,
        });

        if (isNil(updateResult.errors)) {
          return newTag;
        } else {
          setError(i18n('errors.tag.create'));
          throw new Error('GraphQL Error');
        }
      }
      throw new Error('No workflowTemplate found');
    },
    [componentId, updateWorkflowTemplate, workflowTemplateId],
  );

  return {
    loading: updateLoading,
    error: error ?? (isNil(updateError) ? undefined : i18n('errors.tag.create')),
    addTag,
  };
}
